import { useContext, useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import { Column } from 'react-table'
import Table from '../../../../../../global/components/table/Table'
import TableTopper from '../../../../../../global/components/table/helpers/table-topper/TableTopper'
import TableTitle from '../../../../../../global/components/table/helpers/table-title/TableTitle'
import IncidentReportModal from '../../../../../../incident-reports/components/modal/IncidentReportModal'
import { IIncidentReport } from '../../../../../../../../app/entities/IncidentReport'
import { fetchData } from '../../../../../../global/utils/fetch'
import { getIncidentReportsByOrderObjectId } from '../../../../../../incident-reports/api'
import { WoDetailContext } from '../../../../context/context'

interface IIncidentReportsTableProps {
  isReadOnly: boolean
}

export default function IncidentReportsWoDetailTable({
  isReadOnly,
}: IIncidentReportsTableProps) {
  const { id: orderObjectId } = useParams()
  const { data: orderData } = useContext(WoDetailContext)
  const [tableState, setTableState] = useState({
    data: [] as IIncidentReport[],
    isLoading: false,
  })

  const [isOpen, setIsOpen] = useState(false)
  const [modalOpen, setModalOpen] = useState<boolean>(false)
  const [selectedIncidentReportObjectId, setSelectedIncidentReportObjectId] =
    useState<string | undefined>(undefined)
  const [refreshKey, setRefreshKey] = useState(0)

  function handleOpen() {
    setSelectedIncidentReportObjectId(undefined)
    setModalOpen(true)
  }

  function handleClosed() {
    setSelectedIncidentReportObjectId(undefined)
    setModalOpen(false)
  }

  function onRecordClickHandler(row: any) {
    const { original } = row as { original: IIncidentReport }
    setSelectedIncidentReportObjectId(original.objectId)
    setModalOpen(true)
  }

  async function fetchIncidentReports() {
    if (!orderObjectId) return

    try {
      setTableState((prev) => ({ ...prev, isLoading: true }))
      const data = await fetchData<IIncidentReport[]>(
        getIncidentReportsByOrderObjectId(orderObjectId),
      )
      setTableState({ data: data || [], isLoading: false })
    } catch (error) {
      console.error('Error fetching incident reports:', error)
      setTableState((prev) => ({ ...prev, isLoading: false }))
    }
  }

  useEffect(() => {
    if (isOpen) {
      fetchIncidentReports()
    }
  }, [isOpen, refreshKey, orderObjectId])

  const columns: Column<IIncidentReport>[] = [
    {
      Header: 'Date',
      accessor: 'incidentDate',
      Cell: ({ row: { original } }) => {
        const { incidentDate } = original as IIncidentReport
        if (!incidentDate) return <div>-</div>
        return <div>{new Date(incidentDate).toLocaleDateString()}</div>
      },
    },
    {
      Header: 'Type',
      accessor: 'incidentType',
      Cell: ({ row: { original } }) => {
        const { incidentType } = original as IIncidentReport
        if (!incidentType) return <div>-</div>
        return <div>{incidentType.replace(/_/g, ' ')}</div>
      },
    },
    {
      Header: 'Severity',
      accessor: 'severityOfIncident',
      Cell: ({ row: { original } }) => {
        const { severityOfIncident } = original as IIncidentReport
        if (!severityOfIncident) return <div>-</div>
        return <div>{severityOfIncident}</div>
      },
    },
    {
      Header: 'Technician',
      accessor: 'technician',
      Cell: ({ row: { original } }) => {
        const { technician } = original as IIncidentReport
        if (!technician) return <div>-</div>
        return <div>{technician.name || '-'}</div>
      },
    },
  ]

  return (
    <div style={{ width: '100%' }}>
      <IncidentReportModal
        isOpen={modalOpen}
        handleClosed={handleClosed}
        incidentReportObjectId={selectedIncidentReportObjectId}
        orderObjectId={orderObjectId}
        orderData={orderData}
        onSaveSuccess={() => setRefreshKey(refreshKey + 1)}
      />
      <TableTopper isOpen={isOpen} onToggleClick={() => setIsOpen(!isOpen)}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
          <TableTitle>Incident Reports</TableTitle>
          <button
            onClick={handleOpen}
            style={{
              all: 'unset',
              cursor: isReadOnly ? 'not-allowed' : 'pointer',
              color: isReadOnly ? '#999' : '#d70000',
            }}
            disabled={isReadOnly}
          >
            Add New Incident Report
          </button>
        </div>
      </TableTopper>
      <Table
        columns={columns}
        data={tableState.data}
        isLoading={tableState.isLoading}
        onRowClick={onRecordClickHandler}
        isOpen={isOpen}
        isDisplayMode={true}
      />
    </div>
  )
}
